import { BlockWrapper } from "@/block-system/components";
import { usePageContext } from "lib/context/page-context";
import { useTheme } from "lib/theme";

import { type BlockId } from "block-system/types";
import { type TableBlock as TableBlockType } from "../types";

import {
  CreateTableRecord,
  EditTableRecordModal,
} from "@/block-system/blocks/Table/Block/TableActions";
import { TableBlockSkeleton } from "@/block-system/blocks/Table/Block/TableBlockSkeleton";
import {
  TableRecordsUtilsProvider,
  useTableColumns,
  useTableFields,
  useTableRecords,
} from "@/block-system/blocks/Table/Block/TableData";
import { TablePagination } from "@/block-system/blocks/Table/Block/TablePagination";
import {
  TanstackTable,
  type TanstackTableImperativeAPI,
} from "@/block-system/blocks/Table/Block/TanstackTable";
import { DEFAULT_PAGE_SIZE_NUMBER } from "@/block-system/blocks/Table/constants";
import { StandardBlockErrorState } from "@/block-system/components/BlockErrors/StandardBlockErrorState";
import { StandardBlockZeroState } from "@/block-system/components/BlockErrors/StandardBlockZeroState";
import { SearchInput } from "@/block-system/components/forms/SearchInput";
import { TablePermissionsErrorBoundary } from "@/block-system/components/TablePermissionsErrorBoundary";
import type { Theme } from "@/lib/theme/themes";
import type { APIRecord } from "@/server/services/tables/types/records";
import { type Row, type Table } from "@tanstack/react-table";
import { TableIcon } from "lucide-react";
import { Fragment, useId, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

export function TableBlock({
  block,
  blockId,
}: {
  block: TableBlockType;
  blockId: BlockId;
}) {
  const theme = useTheme();
  const { projectId, pageId, isEmbedded } = usePageContext();

  return (
    <BlockWrapper
      blockId={blockId}
      block={block}
      maxWidth={getMaxBlockWidth({ style: block.config.style, theme })}
      alignment={block.config.style?.alignment}
    >
      <TableErrorBoundary>
        <Table
          config={block.config}
          projectId={projectId}
          pageId={pageId}
          isEmbedded={isEmbedded}
        />
      </TableErrorBoundary>
    </BlockWrapper>
  );
}

function TableErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <TablePermissionsErrorBoundary
      fallback={() => {
        return (
          <StandardBlockErrorState
            data-testid={"table-listing-block-error-state"}
            title="Data not available"
            description="This component is connected to a Table you don't have access to."
          />
        );
      }}
    >
      {children}
    </TablePermissionsErrorBoundary>
  );
}

function getMaxBlockWidth({
  style,
  theme,
}: {
  style: TableBlockType["config"]["style"];
  theme: Theme;
}) {
  if (style?.width != null) {
    return theme.app.width[style.width];
  }

  return theme.app.width.wide;
}

function Table({
  config,
  projectId,
  pageId,
  isEmbedded,
}: {
  config: TableBlockType["config"];
  projectId: string | undefined;
  pageId: string | undefined;
  isEmbedded: boolean;
}) {
  const {
    orders = [],
    filters: blockFilters = [],
    title,
    canSearchRecords: configCanSearchRecords,
    canCreateRecords: configCanCreateRecords,
    canDeleteRecords,
    canUpdateRecords,
    id: blockId,
    tableId,
    columnOrder,
    hiddenFields,
    newRecordButtonCopy,
  } = config;

  const tanstackTableRef = useRef<TanstackTableImperativeAPI>(null);

  const {
    results = [],
    sortingState,
    setSortingState,
    query,
    setQuery,
    isLoadingRecords,
    isFetchingRecords,
    canFetchNextPage,
    canFetchPreviousPage,
    pageSize,
    setPageSize,
    fetchPage,
    canPaginateRecords,
    canSortRecords,
    supportsPageJumping,
    getCurrentQueryInput,
  } = useTableRecords({
    tableId,
    blockId,
    initialOrders: orders,
    blockFilters,
    onPageChange: () => {
      tanstackTableRef.current?.scrollToTop();
    },
    initialPageSize: DEFAULT_PAGE_SIZE_NUMBER,
  });

  const { fields = [], isLoadingFields } = useTableFields({
    blockId,
    tableId,
    hiddenFields,
    columnOrder,
  });

  const columns = useTableColumns({
    fields,
    blockId,
    tableId,
    canUpdateRecords,
    canDeleteRecords,
  });

  /**
   * Currently Tables backend does not support search AND filters
   * Workaround for solution is to hide search when filters are applied and push users to utilize views
   */
  const hasBlockFilters = blockFilters.length > 0;
  const canSearchRecords = configCanSearchRecords && !hasBlockFilters;

  const [selectedRow, setSelectedRow] = useState<Row<APIRecord> | null>(null);

  if (!tableId || !blockId) {
    return (
      <StandardBlockZeroState
        icon={<TableIcon />}
        className="w-full"
        title="No table selected"
        data-testid={`table-listing-block-zero-state`}
      />
    );
  }

  const canCreateRecords =
    configCanCreateRecords === true && projectId != null && pageId != null;

  const isLoadingData = isLoadingRecords || isLoadingFields;
  if (isLoadingData) {
    return (
      <TableBlockSkeleton
        hasTitle={title != null}
        canSearchRecords={canSearchRecords}
        canCreateRecords={canCreateRecords}
      />
    );
  }

  const SearchForm = canSearchRecords ? (
    <TableBlockSearchForm initialQuery={query} onQueryChange={setQuery} />
  ) : null;

  const CreateRecordButton = canCreateRecords ? (
    <CreateTableRecord
      className="ml-auto w-full sm:w-fit"
      newRecordButtonCopy={newRecordButtonCopy}
      tableBlockId={blockId}
      fields={fields}
      projectId={projectId}
      pageId={pageId}
      isEmbedded={isEmbedded}
      tableId={tableId}
    />
  ) : null;

  const Pagination = canPaginateRecords ? (
    <TablePagination
      supportsPageJumping={supportsPageJumping}
      pageSize={pageSize}
      setPageSize={setPageSize}
      fetchPage={fetchPage}
      canFetchPreviousPage={canFetchPreviousPage}
      canFetchNextPage={canFetchNextPage}
      canSetPageSize={!isFetchingRecords}
    />
  ) : null;

  return (
    <div
      className="flex flex-col gap-10"
      data-testid={`table-block-table-${tableId}`}
    >
      <TableRecordsUtilsProvider
        fetchPage={fetchPage}
        getCurrentQueryInput={getCurrentQueryInput}
      >
        <TableBlockHeader
          title={title}
          SearchForm={SearchForm}
          CreateRecordButton={CreateRecordButton}
        />

        {results.length === 0 ? (
          <StandardBlockZeroState
            icon={<TableIcon />}
            title="No records to display"
            className="w-full"
            data-testid={`table-listing-block-zero-state`}
          />
        ) : null}

        {results.length > 0 ? (
          <Fragment>
            {selectedRow ? (
              <EditTableRecordModal
                pageId={pageId as string}
                projectId={projectId as string}
                isEmbedded={isEmbedded}
                tableBlockId={blockId}
                fields={fields}
                record={selectedRow.original}
                onClosed={() => {
                  setSelectedRow(null);
                }}
                tableId={tableId}
              />
            ) : null}
            <TanstackTable
              ref={tanstackTableRef}
              Pagination={Pagination}
              tableId={tableId}
              onRowClick={(row) => {
                if (canUpdateRecords) {
                  setSelectedRow(row);
                }
              }}
              columns={columns}
              data={results}
              isLoading={isFetchingRecords}
              sortingState={sortingState}
              setSortingState={setSortingState}
              enableSorting={canSortRecords}
              title={title}
            />
          </Fragment>
        ) : null}
      </TableRecordsUtilsProvider>
    </div>
  );
}

function TableBlockHeader({
  title,
  SearchForm,
  CreateRecordButton,
}: {
  title: string | undefined;
  SearchForm: React.ReactNode;
  CreateRecordButton: React.ReactNode;
}) {
  if (title == null && SearchForm == null && CreateRecordButton == null) {
    return null;
  }

  const hasSearchForm = SearchForm != null;
  const hasCreateRecordButton = CreateRecordButton != null;

  return (
    <div className="flex flex-col gap-7">
      {title ? <h2 className="leading-7 text-h3">{title}</h2> : null}

      {hasSearchForm || hasCreateRecordButton ? (
        <div className="flex flex-row flex-wrap justify-between gap-2">
          {SearchForm}
          {CreateRecordButton}
        </div>
      ) : null}
    </div>
  );
}

function TableBlockSearchForm({
  initialQuery,
  onQueryChange,
}: {
  initialQuery: string;
  onQueryChange: (query: string) => void;
}) {
  const [internalQuery, setInternalQuery] = useState(initialQuery);

  const waitMs = 500; // The number is completely arbitrary.
  const maxWaitMs = 2_000; // The number is completely arbitrary.
  const debouncedOnQueryChange = useDebouncedCallback(onQueryChange, waitMs, {
    maxWait: maxWaitMs,
  });

  const searchInputId = useId();

  return (
    <search className="w-full sm:w-[248px] md:w-[360px]">
      <form
        className="w-full"
        onSubmit={(event) => {
          /**
           * Make sure that the page does not refresh when the user presses enter.
           *
           * Usually, only the button inside the form could trigger a form submission,
           * but that is not the case when a form contains a single input.
           */
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <label htmlFor={searchInputId} className="sr-only">
          Search for records
        </label>
        <SearchInput
          id={searchInputId}
          value={internalQuery}
          onChange={(value) => {
            setInternalQuery(value);
            debouncedOnQueryChange(value);
          }}
          containerClassName="bg-card w-full"
        />
      </form>
    </search>
  );
}
